// ── Benchmark content textures (lightweight, serialized) ─────────────────────────────────────────────
// Show the REAL thumbnail at EVERY zoom, not a sketch — the same picture you see when the DOM twin mounts
// at high zoom, just smaller when zoomed out. The grid has only ~30 UNIQUE contents (9 slides + 14 notes
// + 7 docs), so each is rasterized to a bitmap ONCE and the whiteboard draws that bitmap for every item.
//
// WHY IT NO LONGER FREEZES THE COLD LOAD: rasterizing one thumbnail means handing full slide HTML to the
// browser as an SVG image, which makes it LAY OUT and PAINT the whole slide — that's real main-thread
// work. Doing all ~30 at once (the old warm-all-up-front) pinned the main thread for seconds → Chrome's
// "Page Unresponsive". So we process a QUEUE: at most ONE rasterization in flight at a time, each kicked
// off during requestIdleCallback. The grid shows the cheap sketch instantly and each real thumbnail pops
// in over the next second or two — progressive hydration, never a burst, never a stall.
//
// WHY IT'S STILL LIGHT (unlike the html-to-image attempt that spun the fans): the templates are already
// fully INLINE-styled, so there's nothing to gather — no cross-origin CSS scan, no @font-face embedding,
// no offscreen mount. A `failed` set means a content that can't rasterize is tried ONCE then falls back
// to the sketch forever — it can never retry-thrash.

import type { BenchKind } from './types'
import { NOTE_FILLS } from './constants'
import { SLIDE_TEMPLATES, NOTE_SAMPLES, DOC_SAMPLES } from './benchContent'
import { loadFontByFamily } from '~/design-system/fonts/loadFont'
import { embedRemoteFontFaces, embedRemoteFontFacesDetailed } from '~/design-system/components/DeckGen/utils/captureSlide'
import { sanitizeSlideHtml, sanitizeDocHtmlForRaster } from '~/design-system/components/DeckGen/utils/sanitizeHtml'
import { isHtmlBakeAvailable, bakeHtmlToBase } from './htmlBake'

// Web fonts the templates reference (Georgia and other system stacks need no embedding). An SVG image is
// SANDBOXED from the page's loaded web fonts, so without embedding it renders Inter as a system fallback —
// and the thumbnail's letter metrics then disagree with the live DOM twin, so text reflows ("jumps") when a
// slide swaps bitmap↔DOM on zoom. Embedding the real font (fetched once, base64'd) makes the two identical,
// so the swap is a pure sharpen. This is the same fix the product's captureSlide uses. Computed ONCE and
// injected into every thumbnail's SVG.
const WEB_FONT_FAMILIES = ['Inter']
let fontEmbedPromise: Promise<string> | null = null
function ensureFontEmbedCSS(): Promise<string> {
  if (!fontEmbedPromise) {
    for (const fam of WEB_FONT_FAMILIES) loadFontByFamily(fam) // inject the provider <link> if not present
    fontEmbedPromise = (async () => {
      await new Promise((r) => setTimeout(r, 60)) // let the injected <link> register its @font-face rules
      try {
        return await embedRemoteFontFaces(WEB_FONT_FAMILIES)
      } catch {
        return ''
      }
    })()
  }
  return fontEmbedPromise
}

const cache = new Map<string, HTMLCanvasElement>()
const mips = new Map<string, HTMLCanvasElement[]>() // per content: [full, half, quarter, …] for crisp downscale
const dominant = new Map<string, string>() // avg color of each texture → the loading-state fill
const failed = new Set<string>() // rasterized once and failed → never retry (no thrash)
let readyListener: (() => void) | null = null

export function setBenchTextureReadyListener(cb: (() => void) | null) {
  readyListener = cb
}

const key = (kind: BenchKind, variant: number) => `${kind}:${variant}`

// Serial work queue: at most one rasterization in flight, next one scheduled on idle time. This is what
// keeps the cold load from freezing — the expensive foreignObject layout never happens in a burst.
type Job = { kind: BenchKind; variant: number; k: string }
const queue: Job[] = []
const queued = new Set<string>()
let busy = false

const onIdle: (cb: () => void) => void =
  typeof (globalThis as { requestIdleCallback?: unknown }).requestIdleCallback === 'function'
    ? (cb) => (globalThis as unknown as { requestIdleCallback: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback(cb, { timeout: 400 })
    : (cb) => setTimeout(cb, 16)

// GESTURE PAUSE: rasterizing a thumbnail lays out + paints a whole slide, which can overrun a frame. Doing
// that WHILE the user scrolls/zooms is what makes the first couple of seconds stutter (the thumbnails all
// warm up and fight the gesture). The canvas flips this true during any scroll/zoom and false ~200ms after
// motion stops, so heavy work only ever runs when you're settled. Both queues (grid + placed slides) honor it.
let rasterPaused = false
export function setRasterPaused(paused: boolean): void {
  const resuming = rasterPaused && !paused
  rasterPaused = paused
  if (resuming) {
    pump()
    pumpHtml()
    pumpImages()
  }
}

function enqueue(kind: BenchKind, variant: number): void {
  const k = key(kind, variant)
  if (cache.has(k) || failed.has(k) || queued.has(k)) return
  queued.add(k)
  queue.push({ kind, variant, k })
  pump()
}

/** Drain the queue one job at a time, each during idle time, so the main thread is never blocked by a
 *  burst of layout+paint. Re-entrant-safe: `busy` guards against starting a second job in parallel. */
function pump(): void {
  if (busy || rasterPaused) return
  const job = queue.shift()
  if (!job) return
  queued.delete(job.k)
  busy = true
  onIdle(() =>
    void rasterize(job, () => {
      busy = false
      if (queue.length) onIdle(pump)
    }),
  )
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

// Native size + capture resolution. Note/doc markup mirrors the DOM twin (items.tsx LabCard) so the
// bitmap and the mounted widget are the same picture (the swap at the mount threshold is invisible).
function spec(
  kind: BenchKind,
  variant: number,
): { html: string; w: number; h: number; texW: number; texH: number } | null {
  if (kind === 'slide') {
    const html = SLIDE_TEMPLATES[variant % SLIDE_TEMPLATES.length]?.html
    if (!html) return null
    return { html, w: 1920, h: 1080, texW: 512, texH: 288 }
  }
  if (kind === 'note') {
    // Real 428px note — fonts/padding identical to the DOM twin (items.tsx LabCard) so bitmap == twin.
    const text = NOTE_SAMPLES[variant % NOTE_SAMPLES.length] ?? ''
    const fill = NOTE_FILLS[(variant + 1) % NOTE_FILLS.length]
    const html = `<div style="width:100%;height:100%;box-sizing:border-box;border-radius:18px;background:${fill};padding:40px;font-family:Inter, ui-sans-serif, system-ui, sans-serif;font-size:28px;line-height:1.45;color:#3f3f46;overflow:hidden">${escapeHtml(text)}</div>`
    return { html, w: 428, h: 428, texW: 512, texH: 512 }
  }
  if (kind === 'doc') {
    // Real 764×1080 doc — fonts/padding identical to the DOM twin (items.tsx LabCard) so bitmap == twin.
    const doc = DOC_SAMPLES[variant % DOC_SAMPLES.length]
    const body = (doc?.body ?? [])
      .map((p) => `<div style="font-size:18px;line-height:1.5;color:#52525b;margin-bottom:14px">${escapeHtml(p)}</div>`)
      .join('')
    const html = `<div style="width:100%;height:100%;box-sizing:border-box;border-radius:8px;background:#fff;box-shadow:inset 0 0 0 1px rgba(0,0,0,0.08);padding:48px;font-family:Georgia, 'Times New Roman', serif;overflow:hidden"><div style="font-size:38px;font-weight:700;color:#18181b;line-height:1.2;margin-bottom:24px">${escapeHtml(doc?.title ?? '')}</div>${body}</div>`
    return { html, w: 764, h: 1080, texW: 512, texH: 724 }
  }
  return null // image kind already has a placeholder bitmap
}

const VALID_XML_NAME = /^[A-Za-z_:][\w.\-:]*$/
// Defensive: if a template has malformed inline HTML (e.g. a double-quote nested inside a double-quoted
// attribute), the lenient HTML parser recovers by inventing junk attributes with invalid names. The DOM
// twin survives that, but the strict SVG/XML re-parse would reject it and the thumbnail blanks. Dropping
// any attribute whose name isn't a legal XML name keeps the serialization well-formed so it still renders.
function stripInvalidAttrs(root: Element): void {
  const els = [root, ...Array.from(root.querySelectorAll('*'))]
  for (const el of els) {
    for (const a of Array.from(el.attributes)) {
      if (!VALID_XML_NAME.test(a.name)) el.removeAttributeNode(a)
    }
  }
}

// The SVG foreignObject is SANDBOXED from the page's stylesheets, so the handful of utility/`dg-text-content`
// classes ElementVisual emits (element-authored slides render through it — see labSlideHtml) would otherwise
// do nothing in the thumbnail: the text box would collapse (no h-full/w-full) and lists lose their markers.
// Inlining just those rules keeps the rasterized thumbnail pixel-identical to the live DOM twin. Harmless for
// the inline-styled benchmark templates (they use none of these classes).
const THUMB_ELEMENT_CSS =
  '.h-full{height:100%}.w-full{width:100%}.m-0{margin:0}.rounded-md{border-radius:.375rem}' +
  '.list-disc{list-style-type:disc}.list-decimal{list-style-type:decimal}.pl-\\[1\\.5em\\]{padding-left:1.5em}' +
  '.dg-text-content{width:100%;height:100%}.dg-text-content ul,.dg-text-content ol{margin:0;padding-left:1.5em}' +
  '.dg-text-content ul{list-style-type:disc}.dg-text-content ol{list-style-type:decimal}.dg-text-content li{margin:0}'

/** Wrap the (inline-styled) markup in an SVG foreignObject, returned as raw SVG markup so the caller
 *  picks the transport (data URL below, or a Blob URL for oversized inputs). XMLSerializer guarantees
 *  valid XML (escapes &, self-closes void tags) from the browser's own HTML parse — no manual fixups. */
function svgMarkup(html: string, w: number, h: number, fontCSS = ''): string {
  const host = document.createElement('div')
  host.setAttribute('xmlns', 'http://www.w3.org/1999/xhtml')
  host.setAttribute('style', `width:${w}px;height:${h}px;overflow:hidden`)
  // Prepend the element-class CSS + the embedded @font-face (data-URL) CSS so the sandboxed SVG renders the
  // REAL web font + element layout, matching the DOM twin. base64/CSS contain no XML-special chars, so
  // serialization stays well-formed.
  host.innerHTML = `<style>${THUMB_ELEMENT_CSS}${fontCSS}</style>` + html // parse-only, never attached → no reflow
  stripInvalidAttrs(host)
  const inner = new XMLSerializer().serializeToString(host)
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}"><foreignObject x="0" y="0" width="100%" height="100%">${inner}</foreignObject></svg>`
}

/** The SVG as a percent-encoded data URL — the historical transport, kept for the bench-grid path. */
function svgUrl(html: string, w: number, h: number, fontCSS = ''): string {
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgMarkup(html, w, h, fontCSS))}`
}

// Build a MIPMAP chain: the full texture, then repeated clean half-scales (512→256→128→…→8). Downscaling
// by ~2x with high-quality smoothing is a real box filter (no aliasing), so a tiny thumbnail drawn from a
// near-right-sized mip stays SHARP — instead of crushing a 512px bitmap straight to 13px (the muddy mess).
function buildMipChain(base: HTMLCanvasElement): HTMLCanvasElement[] {
  const chain: HTMLCanvasElement[] = [base]
  let prev = base
  while (prev.width > 8 && prev.height > 4) {
    const w = Math.max(1, Math.round(prev.width / 2))
    const h = Math.max(1, Math.round(prev.height / 2))
    const c = document.createElement('canvas')
    c.width = w
    c.height = h
    const cx = c.getContext('2d')
    if (!cx) break
    cx.imageSmoothingEnabled = true
    cx.imageSmoothingQuality = 'high'
    cx.drawImage(prev, 0, 0, w, h)
    chain.push(c)
    prev = c
  }
  return chain
}

// Average color of a texture, via a 1×1 downscale (the 2d engine box-filters the whole bitmap into one
// pixel). Used only as the brief loading-state fill before a content's texture has rasterized.
let avgCanvas: HTMLCanvasElement | null = null
function averageColor(src: HTMLCanvasElement): string {
  if (!avgCanvas) {
    avgCanvas = document.createElement('canvas')
    avgCanvas.width = 1
    avgCanvas.height = 1
  }
  const cx = avgCanvas.getContext('2d', { willReadFrequently: true })
  if (!cx) return '#f4f4f5'
  cx.clearRect(0, 0, 1, 1)
  cx.drawImage(src, 0, 0, 1, 1)
  try {
    const d = cx.getImageData(0, 0, 1, 1).data
    return `rgb(${d[0]}, ${d[1]}, ${d[2]})`
  } catch {
    return '#f4f4f5'
  }
}

/** Rasterize ONE content to a cached bitmap, then call `done()` (always, success or fail) so the queue
 *  can advance. The decode is async (off the main thread); the drawImage on load is the only main-thread
 *  cost and it's one item, so it never blocks perceptibly. */
async function rasterize(job: Job, done: () => void): Promise<void> {
  const { kind, variant, k } = job
  if (cache.has(k) || failed.has(k)) {
    done()
    return
  }
  const s = spec(kind, variant)
  if (!s) {
    failed.add(k)
    done()
    return
  }
  // Embed the real web font into the SVG (computed once, cached) so the bitmap matches the DOM twin — the
  // first job waits on the one-time fetch, the rest resolve instantly.
  const fontCSS = await ensureFontEmbedCSS()
  let url: string
  try {
    url = svgUrl(s.html, s.w, s.h, fontCSS)
  } catch {
    failed.add(k)
    done()
    return
  }
  const img = new Image()
  img.decoding = 'async'
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas')
      canvas.width = s.texW
      canvas.height = s.texH
      const cx = canvas.getContext('2d')
      if (!cx) throw new Error('no 2d')
      cx.imageSmoothingEnabled = true
      cx.imageSmoothingQuality = 'high'
      if (s.w > s.texW) {
        // DOWNSCALE case (slides, docs): rasterize the SVG at its INTRINSIC size first so the foreignObject
        // lays text out at the same resolution as the DOM twin, THEN box-filter down into the texture.
        // Drawing the big SVG straight to the small texture makes Chrome hint/space the text at the tiny
        // destination size, so the bitmap reads ~0.8× "more compact" than the mounted DOM — the visible
        // line-spacing bump at the handoff. This forces layout parity: every mip is a pure downscale of the
        // SAME 1920 layout, so mip→DOM is a sharpen, not a reflow.
        const full = document.createElement('canvas')
        full.width = s.w
        full.height = s.h
        const fx = full.getContext('2d')
        if (!fx) throw new Error('no 2d')
        fx.imageSmoothingEnabled = true
        fx.imageSmoothingQuality = 'high'
        fx.drawImage(img, 0, 0, s.w, s.h) // 1:1 → foreignObject lays out at full resolution, like the twin
        cx.drawImage(full, 0, 0, s.texW, s.texH)
      } else {
        // UPSCALE case (notes): the texture is bigger than native, so drawing straight in supersamples the
        // small native layout for a crisp base — and the layout is already at native size, matching the twin.
        cx.drawImage(img, 0, 0, s.texW, s.texH)
      }
      cache.set(k, canvas)
      mips.set(k, buildMipChain(canvas)) // the crisp-at-any-zoom chain
      dominant.set(k, averageColor(canvas)) // 1×1 downscale → the loading-state fill color
      readyListener?.()
    } catch {
      failed.add(k) // tainted/undrawable → fall back to the sketch forever
    }
    done()
  }
  img.onerror = () => {
    failed.add(k) // couldn't parse/decode this content → sketch fallback, never retried
    done()
  }
  img.src = url
}

/** The cached texture for one content, or undefined until it rasterizes. Requesting an uncached one
 *  enqueues its render (once); the whiteboard draws the cheap sketch until it lands. */
export function getBenchTexture(kind: BenchKind, variant: number): HTMLCanvasElement | undefined {
  const hit = cache.get(key(kind, variant))
  if (hit) return hit
  enqueue(kind, variant)
  return undefined
}

/** The mip whose size best matches the on-screen (device-pixel) width you're about to draw — the smallest
 *  one still ≥ the target, so the browser downscales it by ≤2x and the thumbnail stays razor sharp. Returns
 *  undefined until the content has rasterized (requesting it enqueues that). */
export function getBenchMip(
  kind: BenchKind,
  variant: number,
  targetDeviceW: number,
): HTMLCanvasElement | undefined {
  const chain = mips.get(key(kind, variant))
  if (!chain) {
    enqueue(kind, variant)
    return undefined
  }
  let chosen = chain[0] // full-res fallback (target bigger than the base)
  for (const m of chain) {
    if (m.width >= targetDeviceW) chosen = m // walk down while still ≥ target → smallest that qualifies
    else break
  }
  return chosen
}

/** The dominant (average) color of one content's texture, once rasterized — the fill the whiteboard uses
 *  to paint a crisp SILHOUETTE at tiny zoom (where the full render would just be mush). Undefined until the
 *  texture lands; requesting it also enqueues the render so the color becomes known within a beat. */
export function getBenchDominant(kind: BenchKind, variant: number): string | undefined {
  const hit = dominant.get(key(kind, variant))
  if (hit) return hit
  enqueue(kind, variant)
  return undefined
}

/** Queue ALL unique content textures so the grid fills in with real thumbnails on its own, spread across
 *  idle time (one at a time). Idempotent; safe to call on mount — it never blocks first paint. */
export function warmBenchTextures(): void {
  for (let i = 0; i < SLIDE_TEMPLATES.length; i++) enqueue('slide', i)
  for (let i = 0; i < NOTE_SAMPLES.length; i++) enqueue('note', i)
  for (let i = 0; i < DOC_SAMPLES.length; i++) enqueue('doc', i)
}

// ── Placed-slide thumbnails ───────────────────────────────────────────────────────────────────────────
// A user's OWN generated slide gets the SAME real thumbnail as the bench grid — rasterized from its flow
// HTML (fonts baked in), so at overview zoom it shows the actual slide shrunk instead of a flat color block.
// Keyed by shape id + a content hash, so a regenerated slide re-rasterizes. Same serial idle queue as the
// grid, so a burst of generations never blocks the main thread.
// ── Persistent bitmap cache (far-view Slice 5+8, farview=next only) ─────────────────────────────────
// The measured reload problem: mips lived in memory only, so EVERY boot re-rastered every artifact
// (~500ms of main thread each; a 108-artifact deck = 89 long tasks, 36.6s blocked, the felt 6fps).
// Fix: every successful raster writes its BASE mip to IndexedDB as a webp blob keyed by the CONTENT
// key (already content-hashed, so invalidation is automatic — new content is a new key). A cache hit
// on the next boot decodes off-thread and installs the chain with zero raster work. All of it is
// fail-open: any IDB error, quota, or missing API falls back to the raster path silently.
import { farviewNext } from '../birtvy/farviewFlag'

const THUMB_DB_NAME = 'birtvy-thumb-cache'
const THUMB_DB_STORE = 'thumbs'
/**
 * Bumped to 2 on 2026-09-10 TO THROW AWAY WHAT VERSION 1 STORED.
 *
 * The schema is unchanged; the CONTENTS were not trustworthy. Until the fix in `refreshRemoteFontFaces`,
 * a stylesheet that failed to fetch was marked parsed anyway, so the families it carried baked as
 * fallbacks for the rest of that page's life — and every thumbnail rastered in that window was written
 * through to this store. The key is the artifact's HTML, which does not change because a font finally
 * loaded, so those bitmaps came back wrong on every subsequent reload with nothing able to dislodge
 * them: the reported bug, and self-perpetuating.
 *
 * There is no way to tell a poisoned entry from a good one after the fact — the wrong font is only in
 * the pixels — so the store is emptied once on upgrade. The cost is one re-raster per artifact on the
 * next load, which is exactly what the pipeline does for a cold cache anyway.
 */
const THUMB_DB_VERSION = 2
const THUMB_DB_MAX_ENTRIES = 900 // prune target ~700 — a deck is hundreds of contents, not thousands
let thumbDbPromise: Promise<IDBDatabase | null> | null = null

function openThumbDb(): Promise<IDBDatabase | null> {
  if (thumbDbPromise) return thumbDbPromise
  // Abandonment protocol: the race below can give up on this attempt (timeout) while the underlying
  // request stays alive. A LATE success must close its connection immediately — an orphaned open
  // connection blocks every future deleteDatabase/upgrade on the origin, which queues every future
  // open behind it: the whole-origin IDB deadlock we hit live. Same reason the held connection listens
  // for versionchange: when any tab deletes/upgrades, we close and let the next caller re-open.
  let abandoned = false
  const attempt = new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') {
        resolve(null)
        return
      }
      const req = indexedDB.open(THUMB_DB_NAME, THUMB_DB_VERSION)
      req.onupgradeneeded = () => {
        const db = req.result
        // Dropped and recreated rather than migrated: see THUMB_DB_VERSION. A first-time visitor takes
        // the same path minus the delete, so there is one code path and no version arithmetic here.
        if (db.objectStoreNames.contains(THUMB_DB_STORE)) db.deleteObjectStore(THUMB_DB_STORE)
        const store = db.createObjectStore(THUMB_DB_STORE, { keyPath: 'key' })
        store.createIndex('at', 'at')
      }
      req.onsuccess = () => {
        const db = req.result
        if (abandoned) {
          try {
            db.close()
          } catch {
            /* already closing */
          }
          resolve(null)
          return
        }
        db.onversionchange = () => {
          try {
            db.close()
          } catch {
            /* already closing */
          }
          thumbDbPromise = null
        }
        resolve(db)
        pruneThumbDb(db)
      }
      req.onerror = () => resolve(null)
      req.onblocked = () => {
        /* keep waiting — the race timeout decides */
      }
    } catch {
      resolve(null)
    }
  })
  // HARD TIMEOUT: indexedDB.open can hang indefinitely (a pending deleteDatabase, another tab holding
  // a version lock, private-mode quirks). Every cache path funnels through this promise, so a hung
  // open would park keys in the pending set forever and DEADLOCK the raster queue behind it (measured
  // live: cold boot, rendered 0 / queued 0 at 22s). Cache is an optimization — after 1.5s we declare
  // it absent and the raster path proceeds; a later call retries fresh.
  thumbDbPromise = Promise.race([
    attempt,
    new Promise<IDBDatabase | null>((resolve) =>
      setTimeout(() => {
        abandoned = true
        resolve(null)
      }, 1500),
    ),
  ]).then((db) => {
    if (!db) thumbDbPromise = null // let a later caller retry a fresh open
    return db
  })
  return thumbDbPromise
}

/** Bound the store: content keys churn (font css re-keys docs, edits re-key slides), so old entries
 *  accumulate. Prune oldest-first past the cap, asynchronously, best-effort. */
function pruneThumbDb(db: IDBDatabase): void {
  try {
    const tx = db.transaction(THUMB_DB_STORE, 'readwrite')
    const store = tx.objectStore(THUMB_DB_STORE)
    const countReq = store.count()
    countReq.onsuccess = () => {
      let excess = countReq.result - THUMB_DB_MAX_ENTRIES + 200
      if (countReq.result <= THUMB_DB_MAX_ENTRIES) return
      const cursorReq = store.index('at').openCursor()
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result
        if (!cursor || excess <= 0) return
        cursor.delete()
        excess -= 1
        cursor.continue()
      }
    }
  } catch {
    /* best-effort */
  }
}

function thumbDbGet(key: string): Promise<Blob | null> {
  return openThumbDb().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) {
          resolve(null)
          return
        }
        try {
          const req = db.transaction(THUMB_DB_STORE, 'readonly').objectStore(THUMB_DB_STORE).get(key)
          req.onsuccess = () => resolve(req.result ? (req.result.blob as Blob) : null)
          req.onerror = () => resolve(null)
        } catch {
          resolve(null)
        }
      }),
  )
}

/** Small text KV riding the same store (records carry either a bitmap blob or a css string) — used by
 *  docFonts to persist each stylesheet's inlined @font-face css, which otherwise re-fetches and
 *  re-base64s every boot and serially delays the whole doc-thumb arm by seconds. */
export function thumbDbGetText(key: string): Promise<string | null> {
  return openThumbDb().then(
    (db) =>
      new Promise((resolve) => {
        if (!db) {
          resolve(null)
          return
        }
        try {
          const req = db.transaction(THUMB_DB_STORE, 'readonly').objectStore(THUMB_DB_STORE).get(key)
          req.onsuccess = () => resolve(req.result && typeof req.result.css === 'string' ? (req.result.css as string) : null)
          req.onerror = () => resolve(null)
        } catch {
          resolve(null)
        }
      }),
  )
}

export function thumbDbPutText(key: string, css: string): void {
  if (!farviewNext()) return
  void openThumbDb().then((db) => {
    if (!db) return
    try {
      db.transaction(THUMB_DB_STORE, 'readwrite').objectStore(THUMB_DB_STORE).put({ key, css, at: Date.now() })
    } catch {
      /* fail-open */
    }
  })
}

/** Fire-and-forget write-through after a successful raster. webp at 0.85 keeps a 512-wide base around
 *  30-60KB, so even a large deck stays a few MB. */
// Dev visibility into the write-through (fail-open writes are silent by design, which also makes a
// systematic failure invisible — this ledger is how we caught one). Read as __birtvyThumbDb() below.
const thumbDbLog = { putCalls: 0, blobNull: 0, putOk: 0, putErr: [] as string[], gateSkips: 0, tainted: [] as string[] }

function thumbDbPut(key: string, base: HTMLCanvasElement): void {
  if (!farviewNext()) {
    thumbDbLog.gateSkips += 1
    return
  }
  // A tainted base cannot be exported (and would ALSO taint the GL atlas downstream) — probe before
  // encoding, record which key class taints so a systematic source stays visible, and skip quietly.
  try {
    base.getContext('2d')?.getImageData(0, 0, 1, 1)
  } catch {
    if (thumbDbLog.tainted.length < 12) thumbDbLog.tainted.push(key.slice(0, 16))
    return
  }
  thumbDbLog.putCalls += 1
  try {
    base.toBlob(
      (blob) => {
        if (!blob) {
          thumbDbLog.blobNull += 1
          return
        }
        void openThumbDb().then((db) => {
          if (!db) {
            thumbDbLog.putErr.push('no-db')
            return
          }
          try {
            const req = db.transaction(THUMB_DB_STORE, 'readwrite').objectStore(THUMB_DB_STORE).put({ key, blob, at: Date.now() })
            req.onsuccess = () => {
              thumbDbLog.putOk += 1
            }
            req.onerror = () => {
              thumbDbLog.putErr.push(String(req.error).slice(0, 80))
            }
          } catch (e) {
            thumbDbLog.putErr.push('threw: ' + String((e as Error)?.message).slice(0, 80))
          }
        })
      },
      'image/webp',
      0.85,
    )
  } catch (e) {
    thumbDbLog.putErr.push('toBlob threw: ' + String((e as Error)?.message).slice(0, 80))
  }
}

if (typeof window !== 'undefined') {
  ;(window as unknown as { __birtvyThumbDb: () => unknown }).__birtvyThumbDb = () => ({ ...thumbDbLog, errSample: thumbDbLog.putErr.slice(0, 5) })
  // Pre-open the cache connection at module load — the handshake overlaps app boot instead of adding
  // its latency to the first restore during the loader.
  void openThumbDb()
}

/** Install a cached blob as this key's mip chain — the zero-raster boot path. Decode happens
 *  off-thread (createImageBitmap); only the tiny chain build touches the main thread. */
function installCachedThumb(key: string, blob: Blob, onDone: (ok: boolean) => void): void {
  const finish = (bitmap: ImageBitmap | HTMLImageElement | null) => {
    if (!bitmap) {
      onDone(false)
      return
    }
    try {
      const base = document.createElement('canvas')
      base.width = bitmap.width
      base.height = bitmap.height
      const cx = base.getContext('2d')
      if (!cx) throw new Error('no 2d')
      cx.drawImage(bitmap, 0, 0)
      dominant.set(key, averageColor(base))
      htmlMips.set(key, buildMipChain(base))
      readyListener?.()
      onDone(true)
    } catch {
      onDone(false)
    }
  }
  if (typeof createImageBitmap === 'function') {
    createImageBitmap(blob).then(finish, () => finish(null))
  } else {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      finish(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      finish(null)
    }
    img.src = url
  }
}

const htmlMips = new Map<string, HTMLCanvasElement[]>()
const htmlFailed = new Set<string>()
const htmlQueued = new Set<string>()
/**
 * KEYS BEING RASTERED RIGHT NOW — the guard that was missing, and the gap it left was not small.
 *
 * `htmlQueued` is deleted the instant a job is SHIFTED off the queue (see `pumpHtml`), which is before
 * any of the async work runs. So for the whole duration of a raster its key sat in none of the four
 * sets `requestSlideThumb` checks: not in `htmlMips` (nothing landed yet), not in `htmlFailed`, not in
 * `htmlQueued` (just deleted), and past the IndexedDB branch because `idbMissed` already holds it.
 * A second request for that key therefore fell straight through and enqueued the SAME bytes again.
 *
 * That mattered because `docRaster.rasterizeBake` has no promise to await — it POLLS, every 100ms, up
 * to forty times. Every poll past the first was a fresh enqueue of a raster already in flight.
 *
 * MEASURED, 2026-09-10, one document and one poll loop: `htmlBusyCount` reached 3 concurrent rasters
 * of identical bytes, and `htmlMips` gained exactly one entry — two of the three were pure waste, each
 * a full 1920x1080 foreignObject decode and its own `thumbDbPut`. Nine artifacts opening a deck put
 * that on the same main thread at once.
 *
 * `htmlBusyCount` could not have answered this: it is a NUMBER, so it knows how many rasters are
 * running and not which. This set is the missing half of it.
 */
const htmlInFlight = new Set<string>()
// `doc` marks a baked DOC-SLIDE string. Doc jobs sanitize with the FORCE_BODY variant (their leading
// <style> block must survive) and NEVER take the html-in-canvas rig (see rasterizeSlideThumb). The flag
// rides the job so retries keep it — a doc retry that forgot it would land back on the wrong pipeline.
type ThumbJob = { key: string; html: string; doc?: boolean }
const htmlQueue: ThumbJob[] = []
// Font families whose injected <link> has already been given time to register — so we only pay the settle
// wait the first time a family appears, not on every thumbnail that uses it.
const settledFamilies = new Set<string>()
// Rasterize several thumbnails at once: SVG decodes run async, so a small pool warms a whole deck's
// thumbnails several times faster than one-at-a-time (kills the overview-zoom block flash on load).
const HTML_CONCURRENCY = 3
// A slide whose SVG fails to decode/draw (transient decoder pressure, a slow image, a flaky paint) used to
// be marked dead forever → a permanent empty block at overview. Instead retry a couple of times with backoff;
// only give up (and log) after that. Attempt count is per content key.
const htmlAttempts = new Map<string, number>()
const MAX_THUMB_ATTEMPTS = 3
// Raw-svg size (chars, pre-encoding) above which the placed-slide path ships a Blob URL instead of an
// encodeURIComponent data URL. Typical slide/doc strings are tens-to-hundreds of KB, so the data-URL
// transport stays the norm; the Blob fallback only exists so a pathological doc (huge stylesheet, big
// data-URL images) cannot hit multi-MB data-URL territory, where percent-encoding roughly doubles the
// string and decode failures surface only as the generic `[canvas-lab] thumbnail decode error` retry.
const SVG_DATA_URL_MAX = 1_500_000
let htmlBusyCount = 0

/** Content-hash cache, keyed by string VALUE. The bare charCodeAt walk below re-scanned the WHOLE string
 *  on every call, and the GL far view asks for the same keys relentlessly — every convergence tick,
 *  every applySkip, every glQuads recompute re-keys every quad. A flow slide's html is a few KB; a doc
 *  quad's string is the ~95KB font-raster css + its baked document, so with artifacts on the canvas the
 *  keying itself became the frame budget: measured 2026-08-18 (12-artifact deck, cold session), 56% of
 *  all zoom-sweep CPU samples were quadKey/slideThumbKey, zoom frame p95 216ms vs 9ms flow-only.
 *  The Map leans on V8's cached string hash: same-instance lookups (the convergence loop's case — the
 *  quads array is stable between feed changes) are pointer hits, and value-equal fresh instances (each
 *  thumbTick rebuilds the doc strings) cost one internal hash + one memcmp, both far cheaper than the
 *  JS loop. Entries hold references to strings the deck already retains, so the wholesale clear at cap
 *  bounds only STALE ones (replaced bakes) — same idiom as BirtvySlideCard's docBgCache. */
const hashCache = new Map<string, string>()
function hashStr(s: string): string {
  const hit = hashCache.get(s)
  if (hit !== undefined) return hit
  let h = 0
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0
  const key = `${s.length}.${(h >>> 0).toString(36)}`
  if (hashCache.size > 600) hashCache.clear()
  hashCache.set(s, key)
  return key
}

/** A stable key for a placed slide's current content. Changes when the HTML changes → re-rasterize.
 *  `id` accepts a real slide's string id too (the product store keys slides by uuid), not just the lab's
 *  numeric shape ids. */
export function slideThumbKey(id: string | number, html: string): string {
  return `slide${id}:${hashStr(html)}`
}

// System / generic families the SVG sandbox already renders correctly — no embedding needed (and no fetch).
const SYSTEM_FAMILY =
  /^(system-ui|-apple-system|blinkmacsystemfont|segoe ui|georgia|times|times new roman|arial|helvetica|sans-serif|serif|monospace|ui-sans-serif|ui-serif|ui-monospace|inherit|initial)$/i
function webFamiliesIn(html: string): string[] {
  const out = new Set<string>()
  const re = /font-family\s*:\s*['"]?([^;"'{}]+)/gi
  let m: RegExpExecArray | null
  while ((m = re.exec(html))) {
    const first = m[1].split(',')[0].replace(/["']/g, '').trim()
    if (first && !SYSTEM_FAMILY.test(first)) out.add(first)
  }
  return [...out]
}

/** The mip best matching the on-screen device width for a placed slide, or undefined until it rasterizes
 *  (requesting it enqueues the render, exactly like the grid). Pass `opts.doc` for a baked doc-slide
 *  string: its bake sanitizes with the FORCE_BODY variant (leading <style> survives — without it the
 *  stylesheet is silently deleted and the thumbnail renders unstyled) and stays off the html-in-canvas
 *  rig (which would mount the doc's stylesheet LIVE in the app). Omitting `targetDeviceW` returns the
 *  full-res base mip; callers that pass a number get the exact pre-existing selection. */
export function getSlideThumbMip(
  key: string,
  html: string,
  targetDeviceW?: number,
  opts?: { doc?: boolean },
): HTMLCanvasElement | undefined {
  const chain = htmlMips.get(key)
  if (!chain) {
    requestSlideThumb(key, html, opts)
    return undefined
  }
  let chosen = chain[0]
  if (targetDeviceW !== undefined) {
    for (const m of chain) {
      if (m.width >= targetDeviceW) chosen = m
      else break
    }
  }
  return chosen
}

// Keys with an IDB lookup in flight (value carries the doc flag so an upgrade mid-lookup survives),
// and keys the cache has already missed on (so later requests go straight to the raster queue instead
// of re-asking the DB every render).
const idbPendingLookups = new Map<string, { doc?: boolean }>()
const idbMissed = new Set<string>()

/** Far-view Slice 5: restore a whole wanted-key set from the persistent cache in ONE transaction.
 *  The loader's warm sweep knows every key the far view wants at arm time; asking IndexedDB per key
 *  costs a round-trip each exactly while the loader is counting, one shared transaction costs
 *  milliseconds total. Hits install their chains (readyListener coalesces the repaint); misses are
 *  marked AND enqueued directly into the raster queue with the context they carry (bulk runs BEFORE
 *  warmSlideThumbs, whose per-key requests then dedupe against the pending set — so a miss must not
 *  rely on a later request that already returned early). Fire-and-forget, fail-open. */
export function bulkRestoreThumbs(entries: Array<{ key: string; html?: string; doc?: boolean; src?: string }>): void {
  if (!farviewNext() || entries.length === 0) return
  const want = entries.filter(
    (e) => !htmlMips.has(e.key) && !htmlFailed.has(e.key) && !idbMissed.has(e.key) && !idbPendingLookups.has(e.key) && !htmlQueued.has(e.key) && !imageQueued.has(e.key),
  )
  if (want.length === 0) return
  for (const e of want) idbPendingLookups.set(e.key, { doc: e.doc })
  const enqueueMiss = (e: { key: string; html?: string; doc?: boolean; src?: string }) => {
    idbPendingLookups.delete(e.key)
    idbMissed.add(e.key)
    if (e.src) {
      if (!imageQueued.has(e.key)) {
        imageQueued.add(e.key)
        imageQueue.push({ key: e.key, src: e.src })
        pumpImages()
      }
    } else if (e.html) {
      if (!htmlQueued.has(e.key)) {
        htmlQueued.add(e.key)
        htmlQueue.push({ key: e.key, html: e.html, doc: e.doc })
        pumpHtml()
      }
    }
  }
  void openThumbDb().then((db) => {
    if (!db) {
      for (const e of want) enqueueMiss(e)
      return
    }
    try {
      const store = db.transaction(THUMB_DB_STORE, 'readonly').objectStore(THUMB_DB_STORE)
      for (const e of want) {
        const req = store.get(e.key)
        req.onsuccess = () => {
          const blob = req.result ? (req.result.blob as Blob) : null
          if (blob) {
            installCachedThumb(e.key, blob, (ok) => {
              idbPendingLookups.delete(e.key)
              if (!ok) enqueueMiss(e)
            })
          } else {
            enqueueMiss(e)
          }
        }
        req.onerror = () => enqueueMiss(e)
      }
    } catch {
      for (const e of want) enqueueMiss(e)
    }
  })
}

function requestSlideThumb(key: string, html: string, opts?: { doc?: boolean }): void {
  // Already rendered, already given up on, or ALREADY BEING RENDERED. The third is the one that was
  // missing — see `htmlInFlight` for what it cost.
  if (htmlMips.has(key) || htmlFailed.has(key) || htmlInFlight.has(key)) return
  // Far-view Slice 5+8: try the persistent cache first — a hit installs the chain with zero raster
  // work (the whole point of the cache); a miss marks the key and falls through to the queue.
  if (farviewNext() && !idbMissed.has(key) && !htmlQueued.has(key)) {
    const pending = idbPendingLookups.get(key)
    if (pending) {
      if (opts?.doc) pending.doc = true
      return
    }
    const record: { doc?: boolean } = { doc: opts?.doc }
    idbPendingLookups.set(key, record)
    void thumbDbGet(key).then((blob) => {
      const settle = (installed: boolean) => {
        idbPendingLookups.delete(key)
        if (!installed) {
          idbMissed.add(key)
          requestSlideThumb(key, html, record.doc ? { doc: true } : undefined)
        }
      }
      if (blob) installCachedThumb(key, blob, settle)
      else settle(false)
    })
    return
  }
  if (htmlQueued.has(key)) {
    // A doc-flagged request for a key that is still WAITING upgrades the pending job. The cache is
    // content-keyed with no doc dimension (correct: doc html and slide html are different strings), so
    // if an unflagged call site (GL far view, prewarm, capture-ref) enqueued this content first, its
    // job would bake through the wrong pipeline and the flagged caller would be stuck with the broken
    // bitmap forever. Upgrading is always safe: the doc pipeline (SVG-only + FORCE_BODY sanitize) is
    // rendering-correct for doc strings and gracefully equivalent for ordinary body-only html.
    if (opts?.doc) {
      for (const j of htmlQueue) if (j.key === key) j.doc = true
    }
    return
  }
  htmlQueued.add(key)
  htmlQueue.push({ key, html, doc: opts?.doc })
  pumpHtml()
}

/** Eagerly queue thumbnails for a set of slides (e.g. a freshly loaded deck) so they rasterize up front
 *  rather than only when first drawn — so overview zoom (2%) fills in with real thumbnails right away.
 *  Mark baked doc-slide entries with `doc: true` — an unflagged doc warm would bake the style-stripped
 *  bitmap first and the content-keyed cache would then serve it to every flagged caller. */
export function warmSlideThumbs(entries: Array<{ id: string | number; html?: string; doc?: boolean }>): void {
  for (const e of entries) {
    if (e.html) requestSlideThumb(slideThumbKey(e.id, e.html), e.html, e.doc ? { doc: true } : undefined)
  }
}

/** How many of the given thumbnail keys have finished (built OR permanently failed) — lets a loading
 *  screen hold until a freshly loaded deck's overview is populated, without hanging on a failed slide. */
export function countReadySlideThumbs(keys: string[]): number {
  let n = 0
  for (const k of keys) if (htmlMips.has(k) || htmlFailed.has(k)) n++
  return n
}

/** Raster jobs queued or in flight right now. The load screen holds on this as well as its own key
 *  list, because keys re-enter the queue under NEW identities when the font css lands mid-boot (css is
 *  part of the content key) — bookkeeping by key alone declares victory while the queue still churns.
 *  Image ingests (below) are deliberately NOT counted: the loader's contract is untouched until the
 *  far-view Slice 5 unifies progress, and a slow remote image must not hold the load screen today. */
export function pendingSlideThumbWork(): number {
  return htmlQueue.length + htmlBusyCount
}

// ── Canvas IMAGE ingestion (far-view Slice 1) ────────────────────────────────────────────────────────
// A placed image's far-view texture is the decoded image itself — no bake pipeline, no SVG, no rig: draw
// the bitmap into a ≤512 base (aspect preserved) and build the same mip chain slides get. Chains land in
// the SAME store as slide thumbs (htmlMips/htmlFailed) so the GL atlas, ready listener, and content-key
// discipline are shared; only the production queue is separate (a network fetch is not layout work, and
// it must stay invisible to pendingSlideThumbWork — see above).
//
// Cross-origin: loaded with crossOrigin='anonymous' ONLY. A host that refuses CORS fails onerror and the
// key is marked failed after retries — the card's dominant-color fill stands in forever rather than a
// tainted bitmap ever reaching the atlas (one tainted cell kills the whole upload; see the taint belt in
// birtvyGLThumbs). data:/blob: sources (the local Media pool, pastes) are unaffected by the attribute.

const IMAGE_THUMB_BASE = 512 // long side of the base mip — same crispness budget as slide thumbs
const IMAGE_CONCURRENCY = 3
const MAX_IMAGE_ATTEMPTS = 3
type ImageThumbJob = { key: string; src: string }
const imageQueue: ImageThumbJob[] = []
const imageQueued = new Set<string>()
const imageAttempts = new Map<string, number>()
let imageBusyCount = 0

/** A stable content key for a placed image. Keyed by source URL — canvas images are immutable behind
 *  their src (a new upload is a new src), so clones of one image collapse to one atlas cell. */
export function imageThumbKey(src: string): string {
  return `img:${hashStr(src)}`
}

/** The mip best matching the on-screen device width for a placed image, or undefined until it decodes
 *  (requesting it enqueues the load, exactly like slide thumbs). */
export function getImageThumbMip(key: string, src: string, targetDeviceW?: number): HTMLCanvasElement | undefined {
  const chain = htmlMips.get(key)
  if (!chain) {
    requestImageThumb(key, src)
    return undefined
  }
  let chosen = chain[0]
  if (targetDeviceW !== undefined) {
    for (const m of chain) {
      if (m.width >= targetDeviceW) chosen = m
      else break
    }
  }
  return chosen
}

/** The dominant (average) color of an ingested image — the shell's honest fill before/without a texture
 *  (and forever, for CORS-refused sources). Undefined until the first successful decode. */
export function getImageThumbDominant(key: string): string | undefined {
  return dominant.get(key)
}

function requestImageThumb(key: string, src: string): void {
  if (htmlMips.has(key) || htmlFailed.has(key) || imageQueued.has(key)) return
  // Same cache-first path as slide thumbs (images always run under the flag — their far-view quads
  // only exist with farview=next).
  if (farviewNext() && !idbMissed.has(key)) {
    if (idbPendingLookups.has(key)) return
    idbPendingLookups.set(key, {})
    void thumbDbGet(key).then((blob) => {
      const settle = (installed: boolean) => {
        idbPendingLookups.delete(key)
        if (!installed) {
          idbMissed.add(key)
          requestImageThumb(key, src)
        }
      }
      if (blob) installCachedThumb(key, blob, settle)
      else settle(false)
    })
    return
  }
  imageQueued.add(key)
  imageQueue.push({ key, src })
  pumpImages()
}

function pumpImages(): void {
  if (rasterPaused) return
  while (imageBusyCount < IMAGE_CONCURRENCY && imageQueue.length) {
    const job = imageQueue.shift()!
    imageQueued.delete(job.key)
    imageBusyCount++
    ingestImageThumb(job, () => {
      imageBusyCount--
      if (imageQueue.length) pumpImages()
    })
  }
}

function imageFailedOrRetry(job: ImageThumbJob, reason: string, done: () => void): void {
  const attempts = (imageAttempts.get(job.key) ?? 0) + 1
  imageAttempts.set(job.key, attempts)
  if (attempts < MAX_IMAGE_ATTEMPTS) {
    imageQueued.add(job.key) // reserve so requestImageThumb won't double-add while we back off
    window.setTimeout(() => {
      imageQueue.push(job)
      pumpImages()
    }, 300 * attempts)
    done()
    return
  }
  console.warn(`[canvas-lab] image thumb PERMANENTLY failed (${reason})`, job.key)
  htmlFailed.add(job.key)
  done()
}

function ingestImageThumb(job: ImageThumbJob, done: () => void): void {
  if (htmlMips.has(job.key) || htmlFailed.has(job.key)) {
    done()
    return
  }
  const img = new Image()
  img.crossOrigin = 'anonymous'
  img.decoding = 'async'
  img.onload = () => {
    try {
      const nw = img.naturalWidth
      const nh = img.naturalHeight
      if (!nw || !nh) throw new Error('empty decode')
      const scale = Math.min(1, IMAGE_THUMB_BASE / Math.max(nw, nh))
      const base = document.createElement('canvas')
      base.width = Math.max(8, Math.round(nw * scale))
      base.height = Math.max(8, Math.round(nh * scale))
      const cx = base.getContext('2d')
      if (!cx) throw new Error('no 2d')
      cx.imageSmoothingEnabled = true
      cx.imageSmoothingQuality = 'high'
      cx.drawImage(img, 0, 0, base.width, base.height)
      // Last-resort taint probe (mirrors the slide-thumb belt). crossOrigin='anonymous' should make taint
      // impossible — a refusing host errors instead — but one poisoned mip would kill the whole atlas
      // upload, so probe anyway before the chain enters the shared store.
      base.getContext('2d')!.getImageData(0, 0, 1, 1)
      dominant.set(job.key, averageColor(base))
      htmlMips.set(job.key, buildMipChain(base))
      thumbDbPut(job.key, base)
      readyListener?.()
      done()
    } catch (e) {
      imageFailedOrRetry(job, `draw error (${(e as Error)?.message ?? 'unknown'})`, done)
    }
  }
  img.onerror = () => imageFailedOrRetry(job, 'decode/CORS error', done)
  img.src = job.src
}

// Far-view Slice 5: the eager mode above was written assuming the loader covers the screen for the
// whole warm. At 100+ artifacts the loader's 8s cap reveals the live canvas INTO the warm storm
// (measured: 89 long tasks, 36.6s of blocked main thread after one reload). The canvas flips this
// false at reveal (farview=next only), and the pump then runs ONE job per idle callback instead of
// hammering — first paint still comes fast from the IDB cache; only genuinely new content rasters,
// and it yields to gestures. Flag off never flips it, keeping today's behavior byte-identical.
let eagerThumbWarm = true
export function setThumbWarmEager(on: boolean): void {
  const resuming = !eagerThumbWarm && on
  eagerThumbWarm = on
  if (resuming) pumpHtml()
}
// Far-view Slice 5: while the loader covers the screen there is no visible frame to protect, so the
// cold warm may run WIDE — 8 jobs in flight instead of 3 cuts a 108-artifact first load from ~27s to
// inside the loader's 8s window (decodes are async/off-thread; only the 512 draws serialize on the
// main thread). After reveal (and always with the flag off) the cap stays 3.
const htmlConcurrency = () => (farviewNext() && eagerThumbWarm ? 8 : HTML_CONCURRENCY)

let idleHtmlPumpScheduled = false
function scheduleIdleHtmlPump(): void {
  if (idleHtmlPumpScheduled || !htmlQueue.length || htmlBusyCount >= htmlConcurrency()) return
  idleHtmlPumpScheduled = true
  onIdle(() => {
    idleHtmlPumpScheduled = false
    if (rasterPaused || !htmlQueue.length || htmlBusyCount >= htmlConcurrency()) return
    // ONE job per idle tick, deliberately. A burst variant (fill the whole budget per tick) ran 3
    // rasters back-to-back every tick and ground the live canvas to 2-4fps for the length of the
    // drain — the frozen feel Guri reported. Post-reveal work is late arrivals only now (the loader
    // holds until the deck's own set is done), so gentle pacing costs nothing visible.
    const job = htmlQueue.shift()!
    htmlQueued.delete(job.key)
    // Claimed the moment it leaves the queue, so the window between "no longer queued" and "landed in
    // htmlMips" is covered instead of open. See `htmlInFlight`.
    htmlInFlight.add(job.key)
    htmlBusyCount++
    void rasterizeSlideThumb(job, () => {
      htmlInFlight.delete(job.key)
      htmlBusyCount--
      if (htmlQueue.length) pumpHtml()
    })
    scheduleIdleHtmlPump()
  })
}

function pumpHtml(): void {
  if (rasterPaused) return
  if (!eagerThumbWarm && farviewNext()) {
    scheduleIdleHtmlPump()
    return
  }
  while (htmlBusyCount < htmlConcurrency() && htmlQueue.length) {
    const job = htmlQueue.shift()!
    htmlQueued.delete(job.key)
    // Same claim as the idle pump's, for the same reason — see `htmlInFlight`.
    htmlInFlight.add(job.key)
    htmlBusyCount++
    // Warm real-slide thumbnails EAGERLY — no requestIdleCallback. During first load the loader covers the
    // screen, so there is no visible frame to protect, and idle callbacks get starved while the page mounts
    // (~5k DOM nodes) — which is what made a 70-slide overview crawl in over ~15s. Kick each job now; the SVG
    // decode itself is async/off-thread, and the gesture pause (rasterPaused) still guards live interaction.
    void rasterizeSlideThumb(job, () => {
      htmlInFlight.delete(job.key)
      htmlBusyCount--
      if (htmlQueue.length) pumpHtml()
    })
  }
}

/** A thumbnail failed to rasterize. Retry it (with backoff) up to MAX_THUMB_ATTEMPTS before giving up, so a
 *  transient decode/paint failure self-heals instead of leaving a permanent empty block. Logs each event so
 *  a genuinely un-rasterizable slide is visible in the console. Re-queues the SAME job object so the `doc`
 *  flag survives — a doc retry that lost it would re-enter the wrong (rig/plain-sanitize) pipeline. */
function thumbFailedOrRetry(job: ThumbJob, reason: string, done: () => void): void {
  const { key } = job
  const attempts = (htmlAttempts.get(key) ?? 0) + 1
  htmlAttempts.set(key, attempts)
  if (attempts < MAX_THUMB_ATTEMPTS) {
    console.warn(`[canvas-lab] thumbnail ${reason} — retry ${attempts}/${MAX_THUMB_ATTEMPTS - 1}`, key)
    htmlQueued.add(key) // reserve so requestSlideThumb won't also re-add while we wait to re-enqueue
    window.setTimeout(() => {
      htmlQueue.push(job)
      pumpHtml()
    }, 200 * attempts) // back off so transient decoder pressure can subside before we try again
    done()
    return
  }
  console.warn(`[canvas-lab] thumbnail PERMANENTLY failed (${reason}) after ${attempts} attempts`, key)
  htmlFailed.add(key)
  done()
}

/** src → in-flight/inlined data-URI for a DOC thumbnail's external <img> (null = fetch failed). An SVG
 *  image loads NO external subresources — by spec — so an artifact's logo <img> rendered as a blank box
 *  in every far-view thumbnail while the live document showed it fine (Guri, 2026-08-18: "when I zoom
 *  out it breaks … the logos inside"). Inlining as data: URIs is the one transport that both renders
 *  inside the sandboxed SVG and stays origin-clean (data: cannot taint — the whole pipeline's discipline).
 *  Bytes come through the same-origin image proxy, the hop built exactly for CORS-less hosts. Promises
 *  are cached so concurrent rasters of a deck share one fetch per URL; failures are dropped from the
 *  cache so a transient error retries on the next raster instead of pinning a blank logo forever. */
const docImgInlineCache = new Map<string, Promise<string | null>>()
const DOC_IMG_INLINE_MAX_BYTES = 400_000
function inlinedDocImg(src: string): Promise<string | null> {
  let p = docImgInlineCache.get(src)
  if (!p) {
    p = (async (): Promise<string | null> => {
      try {
        // Root-relative srcs ARE same-origin app routes (the logo pass writes proxy-relative urls, 101
        // chars of `/deckgen-image-proxy-api?url=…`) — fetch them as-is with the session's cookies;
        // wrapping them in the proxy again would double-encode. Absolute urls go through the proxy.
        const res = await fetch(src.startsWith('/') ? src : `/deckgen-image-proxy-api?url=${encodeURIComponent(src)}`)
        if (!res.ok) return null
        const blob = await res.blob()
        if (blob.size === 0 || blob.size > DOC_IMG_INLINE_MAX_BYTES) return null
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const fr = new FileReader()
          fr.onload = () => resolve(String(fr.result))
          fr.onerror = () => reject(fr.error)
          fr.readAsDataURL(blob)
        })
        // A proxy that answers without a real image content-type still usually carries image bytes —
        // relabel as png so the SVG decoder at least attempts it (worst case: blank box, the status quo).
        return blob.type.startsWith('image/') ? dataUrl : dataUrl.replace(/^data:[^;,]*/, 'data:image/png')
      } catch {
        return null
      }
    })()
    docImgInlineCache.set(src, p)
    void p.then((v) => {
      if (v === null) docImgInlineCache.delete(src)
    })
  }
  return p
}

/** Rewrite every external <img src> in an (already sanitized) doc raster markup to its inlined data
 *  URI. Unresolvable srcs stay as-is (the SVG ignores them — the pre-inline behavior); `failed` counts
 *  them so the caller can refuse to PERSIST the logo-less bitmap: the mip cache is keyed by content,
 *  the content doesn't change when a fetch recovers, so one bad network moment (measured live: Guri's
 *  first /canvas load right after a manual dev-server restart) would otherwise pin a logo-less stamp
 *  forever — in IndexedDB, across reloads. */
async function inlineDocImagesForRaster(markup: string): Promise<{ markup: string; failed: number }> {
  const srcs = new Set<string>()
  // https:// AND root-relative (single leading slash — same-origin app routes like the kit mark's
  // /api/v1/storage paths). Protocol-relative `//host` is excluded; a data: src needs no inlining.
  for (const m of markup.matchAll(/<img\b[^>]*\bsrc="((?:https?:\/\/|\/(?!\/))[^"]+)"/gi)) srcs.add(m[1])
  if (srcs.size === 0) return { markup, failed: 0 }
  const inlined = new Map<string, string | null>()
  await Promise.all([...srcs].map(async (src) => inlined.set(src, await inlinedDocImg(src))))
  let failed = 0
  for (const v of inlined.values()) if (v === null) failed += 1
  const out = markup.replace(/(<img\b[^>]*\bsrc=")((?:https?:\/\/|\/(?!\/))[^"]+)(")/gi, (whole, pre: string, src: string, post: string) => {
    const data = inlined.get(src)
    return data ? pre + data + post : whole
  })
  return { markup: out, failed }
}

/** One delayed re-raster per content key whose RESOURCES did not arrive: drop the in-memory mip (the
 *  raster entry point early-returns on a present mip, so deletion IS the re-arm) and re-enqueue. The
 *  stamp rests on the card's background fill for the beat between drop and fresh landing. One retry
 *  only — a permanently dead src must not spin the queue; the bitmap still served, it is just never
 *  PERSISTED (thumbDbPut skipped), so the next boot re-rasters it with, hopefully, a live network.
 *
 *  TWO KINDS OF MISSING RESOURCE reach this, and they fail the same way: an `<img>` whose bytes could not
 *  be inlined renders blank, and a webfont whose stylesheet could not be fetched renders the text in a
 *  fallback. Both are wrong pixels caused by the network rather than by the document, and the one thing
 *  that must not happen to either is being written to the persistent cache — that cache is keyed on the
 *  artifact's HTML, so a wrong bitmap comes back on every future reload with nothing to dislodge it.
 *  `doc` rides along because it decides which sanitizer the re-raster uses; hard-coding it was safe only
 *  while images were the only caller. */
const thumbResourceRetriedKeys = new Set<string>()
function scheduleThumbRetry(key: string, html: string, doc?: boolean): void {
  if (thumbResourceRetriedKeys.has(key)) return
  thumbResourceRetriedKeys.add(key)
  window.setTimeout(() => {
    htmlMips.delete(key)
    requestSlideThumb(key, html, { doc })
  }, 8000)
}

async function rasterizeSlideThumb(job: ThumbJob, done: () => void): Promise<void> {
  const { key, html, doc } = job
  if (htmlMips.has(key) || htmlFailed.has(key)) {
    done()
    return
  }
  // PRIMARY: HTML-in-canvas (drawElementImage) — paints REAL HTML into an origin-clean canvas via the browser's
  // own pipeline. No SVG <foreignObject> decode, so it doesn't hit the intermittent blank/taint failures that
  // left slides as empty blocks. Falls through to the SVG path below when the API is off (flag) or a capture
  // comes back blank/timed-out for this specific slide.
  //
  // DOC INPUTS NEVER TAKE THIS PATH. The rig mounts its input LIVE in the app document (htmlBake keeps its
  // host div inside document.body) and never clears the host's innerHTML after capture — a baked doc string's
  // preserved stylesheet (including its `* { animation: none !important }` rule) would restyle the entire
  // app. And the rig's plain sanitize would delete that leading <style> anyway (parser hoists it to <head>,
  // DOMPurify drops it), baking the exact unstyled thumbnail this flag exists to fix. Docs go through the
  // SVG-foreignObject path below exclusively: the SVG image is sandboxed, so its styles cannot escape, and
  // that pipeline is proven pixel-perfect for doc strings.
  if (!doc && isHtmlBakeAvailable()) {
    try {
      const wrapped = `<div style="width:1920px;height:1080px;background:#ffffff;overflow:hidden">${sanitizeSlideHtml(html)}</div>`
      const base = await bakeHtmlToBase(wrapped)
      if (base) {
        // TAINT PROBE before the capture enters the cache. The rig paints REAL HTML, so a subtree that
        // pulled a cross-origin resource (a remote <img> without CORS headers, a web font) comes back as
        // a poisoned canvas — and one drawImage of a poisoned mip taints the GL atlas downstream until
        // texImage2D dies with a SecurityError and takes the whole canvas to the error screen (seen
        // live). Reading one pixel throws on taint and costs nothing when clean; a tainted capture falls
        // through to the SVG pipeline below, which cannot load external resources at all and therefore
        // cannot taint — that slide's remote images render as blank boxes, the honest trade for a
        // far-view bitmap that can actually be uploaded.
        let clean = true
        try {
          base.getContext('2d')?.getImageData(0, 0, 1, 1)
        } catch {
          clean = false
        }
        if (clean) {
          htmlMips.set(key, buildMipChain(base))
          thumbDbPut(key, base)
          readyListener?.()
          done()
          return
        }
        console.warn('[canvas-lab] rig capture tainted (cross-origin subresource) — SVG fallback', key)
      }
    } catch {
      /* fall through to the SVG fallback below */
    }
  }
  // Embed the real fonts this slide uses (plus Inter) so the thumbnail matches the live slide, then wrap the
  // sanitized flow HTML in the 1920x1080 box exactly like the DOM twin does.
  const fams = webFamiliesIn(html)
  for (const f of fams) loadFontByFamily(f)
  // Wait for each font's injected <link> to register ONCE (per family), not once per thumbnail. A whole
  // deck shares a handful of brand fonts, so paying the 60ms settle per slide made a 50-slide deck take
  // seconds to fill in at overview zoom; settling per family cuts that to a single wait.
  const unsettled = fams.filter((f) => !settledFamilies.has(f))
  let fontCSS = ''
  /**
   * Did every family this artifact uses actually make it into the SVG?
   *
   * An SVG image loads no external subresource by spec, so a family that is not embedded here is not
   * merely late — it is a fallback face in the finished bitmap. On a reload every stylesheet is fetched
   * at once and some of those fetches lose, which is why only SOME artifacts came back wrong and why a
   * different set came back wrong each time.
   */
  let fontEmbedIncomplete = false
  try {
    if (unsettled.length) {
      await new Promise((r) => setTimeout(r, 60))
      for (const f of unsettled) settledFamilies.add(f)
    }
    const embedded = await embedRemoteFontFacesDetailed([...new Set([...fams, 'Inter'])])
    fontCSS = embedded.css
    fontEmbedIncomplete = embedded.missing.length > 0
  } catch {
    fontCSS = ''
    // A throw is not "this artifact has no web fonts", it is "we do not know" — and the whole point of
    // the flag is that not knowing must never be persisted as if it were an answer.
    fontEmbedIncomplete = fams.length > 0
  }
  // A family that lost this round must not be treated as settled, or the next artifact using it would
  // skip even the short wait above and lose faster.
  if (fontEmbedIncomplete) for (const f of fams) settledFamilies.delete(f)
  // Docs sanitize with the FORCE_BODY variant: the plain sanitizer parses input as a full document, the
  // parser hoists a doc's LEADING <style> into <head>, and DOMPurify silently deletes it — the proven
  // unstyled-far-thumbnail bug. Safe here ONLY because the SVG image is sandboxed from the page; the
  // FORCE_BODY output must never reach a light-DOM call site (see sanitizeDocHtmlForRaster's contract).
  let wrapped = `<div style="width:1920px;height:1080px;background:#ffffff;overflow:hidden">${doc ? sanitizeDocHtmlForRaster(html) : sanitizeSlideHtml(html)}</div>`
  // Far-view Slice 5+8: strip external <link> tags before the SVG serialization. An SVG image never
  // loads external subresources, so the pixels are identical with or without them — but their mere
  // presence TAINTS the rastered canvas in Chrome, which made the persistent cache unexportable for
  // any artifact carrying its Google Fonts link (measured: 23 of 27 writes refused). Fonts are already
  // embedded via fontCSS data URIs. Flag-gated so the flag-off pipeline stays byte-identical.
  if (farviewNext()) wrapped = wrapped.replace(/<link\b[^>]*>/gi, '')
  // Doc logos/images: the SVG image loads NO external subresources (by spec), so a doc's <img> srcs
  // must be inlined as data: URIs or the far-view stamp renders them blank (the reported
  // logo-missing-at-far-zoom). Tracked so a failed inline is never persisted (see the helper).
  let docImgInlineFailed = false
  if (doc) {
    const inlinedDoc = await inlineDocImagesForRaster(wrapped)
    wrapped = inlinedDoc.markup
    docImgInlineFailed = inlinedDoc.failed > 0
  }
  let url: string
  let objectUrl: string | null = null
  try {
    const markup = svgMarkup(wrapped, 1920, 1080, fontCSS)
    // Far-view Slice 5+8: the Blob-URL transport TAINTS the rastered canvas in current Chrome (verified
    // with a minimal repro: identical SVG, data: = clean, blob: = tainted), which silently made every
    // fonted doc mip unexportable to the persistent cache AND a poison pill for the GL atlas belt. The
    // "origin-clean" claim in the comment below described older Chrome. Under the flag the data: URL
    // transport is used at ANY size — encodeURIComponent of a few hundred KB is ~ms, decode is async,
    // and clean pixels are worth more than the murky large-data-URL concern that motivated the switch.
    if (markup.length > SVG_DATA_URL_MAX && !farviewNext()) {
      // Oversized input (a baked doc can carry a whole document + stylesheet + data-URL images): ship the
      // SAME svg as a Blob URL instead of a data URL. encodeURIComponent inflates the string ~1.5-3x and a
      // multi-MB data: URL is where decode gets slow and browser limits get murky; a same-origin Blob URL
      // has neither problem and keeps the canvas origin-clean. Revoked once the decode settles (both
      // handlers below). Under the threshold the transport is byte-identical to before.
      objectUrl = URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml;charset=utf-8' }))
      url = objectUrl
    } else {
      url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
    }
  } catch {
    console.warn('[canvas-lab] thumbnail serialize failed (permanent)', key)
    htmlFailed.add(key)
    done()
    return
  }
  const img = new Image()
  img.decoding = 'async'
  img.onload = () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl) // decoded — free the blob before the (sync) draw below
    try {
      const canvas = document.createElement('canvas')
      canvas.width = 512
      canvas.height = 288
      const cx = canvas.getContext('2d')
      if (!cx) throw new Error('no 2d')
      cx.imageSmoothingEnabled = true
      cx.imageSmoothingQuality = 'high'
      if (farviewNext()) {
        // Far-view Slice 5: draw the decoded SVG straight into the 512 base. The layout is identical
        // (same 1920 SVG viewport, one scale step with high-quality smoothing); what's skipped is the
        // 1920x1080 intermediate raster, the biggest main-thread chunk in the measured 36.6s-blocked
        // reload storm.
        cx.drawImage(img, 0, 0, 512, 288)
      } else {
        const full = document.createElement('canvas')
        full.width = 1920
        full.height = 1080
        const fx = full.getContext('2d')
        if (!fx) throw new Error('no 2d')
        fx.imageSmoothingEnabled = true
        fx.imageSmoothingQuality = 'high'
        fx.drawImage(img, 0, 0, 1920, 1080) // 1:1 layout parity with the twin, then downscale
        cx.drawImage(full, 0, 0, 512, 288)
      }
      htmlMips.set(key, buildMipChain(canvas))
      // Served, but not stored. Both misses mean the network lost a resource the pixels needed; the
      // cache is keyed on the HTML alone, so persisting now would make this reload's bad luck permanent.
      if (docImgInlineFailed || fontEmbedIncomplete) scheduleThumbRetry(key, html, doc)
      else thumbDbPut(key, canvas)
      readyListener?.()
      done()
    } catch (e) {
      thumbFailedOrRetry(job, `draw error (${(e as Error)?.message ?? 'unknown'})`, done)
    }
  }
  img.onerror = () => {
    if (objectUrl) URL.revokeObjectURL(objectUrl)
    thumbFailedOrRetry(job, 'decode error', done)
  }
  img.src = url
}

// DIAGNOSTIC (dev): one-shot report of the placed-slide thumbnail pipeline, callable from the console as
// `__canvasLabThumbs()`. Tells us exactly how many slide thumbnails rendered vs permanently failed (and their
// keys) without needing the browser extension — the fast way to confirm what's showing as an empty block.
if (typeof window !== 'undefined') {
  ;(window as unknown as { __canvasLabThumbs: () => unknown }).__canvasLabThumbs = () => ({
    rendered: htmlMips.size,
    permanentlyFailed: htmlFailed.size,
    failedKeys: [...htmlFailed],
    retrying: [...htmlAttempts.entries()].filter(([k]) => !htmlMips.has(k) && !htmlFailed.has(k)),
    queued: htmlQueue.length,
    inflight: htmlBusyCount,
  })
}
